import logdna from '@logdna/browser'

const CSRF_TOKEN_ERROR = {
  type: 'csrf',
  error: 'Please sign in again to continue'
}
const CSRF_ERROR_MESSAGE = 'misconfigured csrf'
const CSRF_METHODS = [
  'delete',
  'patch',
  'post',
  'put'
]

function createSearchParams(params) {
  if (params == null) {
    return new URLSearchParams()
  }
  if (typeof params === 'string') {
    return new URLSearchParams(params)
  }
  if (typeof params === 'object' && !Array.isArray(params)) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, values]) => {
      if (Array.isArray(values)) {
        values.forEach((value) => {
          if (value != null) {
            searchParams.append(key, value)
          }
        })
      } else {
        if (values != null) {
          searchParams.append(key, values)
        }
      }
    })
    return searchParams
  }

  throw new Error('comm.js: Invalid UrlSearchParams')
}

class MezmoFetch {
  csrf
  account
  sessionTraceId

  init(account, csrf, sessionTraceId) {
    this.account = account
    this.csrf = csrf
    this.sessionTraceId = sessionTraceId
  }

  async get(url, options = {}) {
    if (options.params) {
      const params = createSearchParams(options.params)
      url = `${url}?${params.toString()}`
    }

    const _options = {
      method: 'GET',
      ...options
    }

    return this._fetch(url, _options)
  }

  async post(url, body, options = {}) {
    const _options = {
      method: 'POST',
      body,
      ...options
    }
    return this._fetch(url, _options)
  }

  async put(url, body, options = {}) {
    const _options = {
      method: 'PUT',
      body,
      ...options
    }
    return this._fetch(url, _options)
  }

  async patch(url, body, options = {}) {
    const _options = {
      method: 'PATCH',
      body,
      ...options
    }
    return this._fetch(url, _options)
  }

  async delete(url, body, options = {}) {
    const _options = {
      method: 'DELETE',
      body,
      ...options
    }
    return this._fetch(url, _options)
  }

  async postStreaming(url, body, options = {}) {
    const _options = {
      method: 'POST',
      body,
      ...options
    }
    return this._fetchStreaming(url, _options)
  }

  async _fetch(url, options = {}) {
    const _options = { ...options }

    const headers = new Headers(options.headers)
    headers.set('X-Requested-With', 'XMLHttpRequest')
    headers.set('Accept', 'application/json')

    if (this.account) {
      headers.set('X-Account-Context', this.account)
    }
    if (this.sessionTraceId) {
      headers.set('X-Session-Trace-Id', this.sessionTraceId)
    }
    if (CSRF_METHODS.includes(options.method.toLowerCase())) {
      headers.set('X-CSRF-Token', this.csrf)
    }
    _options.headers = headers

    const abortSignals = []
    let timeoutSignal

    if (options.timeout > 0 && 'timeout' in AbortSignal) {
      timeoutSignal = AbortSignal.timeout(options.timeout)
    }

    if ('any' in AbortSignal) {
      if (timeoutSignal) abortSignals.push(timeoutSignal)
      if (options.signal) abortSignals.push(options.signal)
    }

    // If any is supported then use the array of signals
    // or give the timeout signal priority over the
    // user specified signal
    _options.signal = abortSignals.length
      ? AbortSignal.any(abortSignals)
      : (timeoutSignal ?? options.signal)

    try {
      if (options.body) {
        headers.set('Content-Type', 'application/json')
        _options.body = JSON.stringify(options.body)
      }

      const response = await fetch(url, _options)

      if (response.ok) {
        let data
        if (response.status !== 204) {
          data = options.responseType === 'text'
            ? await response.text()
            : await response.json()
        }
        return {
          data
        }
      }

      // Attempt to parse the error response as json and then add it to the error object
      // This is what was done in the original axios comm object and is needed to display
      // errors coming from the ldw backend.
      let err = {}
      try {
        err = await response.json()
      } catch (e) { }

      const { data, status, statusText: message } = response ?? {}
      let error = {
        data,
        status,
        message,
        ...err
      }

      if (data?.message === CSRF_ERROR_MESSAGE) {
        throw new Error(CSRF_TOKEN_ERROR)
      }

      if (status === 401) {
        window.location.href = '/account/signout'
      }

      if (status !== 400) {
        logdna?.error?.(error)
      }

      throw error
    } catch (err) {
      // Error types
      // Fetch Timeout errors are TimeoutError
      // Fetch abort errors are AbortError
      throw err
    }
  }

  async _fetchStreaming(url, options = {}) {
    const _options = { ...options }

    const headers = new Headers(options.headers)
    headers.set('X-Requested-With', 'XMLHttpRequest')
    headers.set('Accept', 'text/event-stream')
    headers.set('Content-Type', 'application/json')

    if (this.account) {
      headers.set('X-Account-Context', this.account)
    }
    if (this.sessionTraceId) {
      headers.set('X-Session-Trace-Id', this.sessionTraceId)
    }
    if (CSRF_METHODS.includes(options.method.toLowerCase())) {
      headers.set('X-CSRF-Token', this.csrf)
    }
    _options.headers = headers

    const abortSignals = []
    let timeoutSignal

    if (options.timeout > 0 && 'timeout' in AbortSignal) {
      timeoutSignal = AbortSignal.timeout(options.timeout)
    }

    if ('any' in AbortSignal) {
      if (timeoutSignal) abortSignals.push(timeoutSignal)
      if (options.signal) abortSignals.push(options.signal)
    }

    // If any is supported then use the array of signals
    // or give the timeout signal priority over the
    // user specified signal
    _options.signal = abortSignals.length
      ? AbortSignal.any(abortSignals)
      : (timeoutSignal ?? options.signal)

    if (options.body) {
      headers.set('Content-Type', 'application/json')
      _options.body = JSON.stringify(options.body)
    }
    try {
      const response = await fetch(url, _options)
      if (response.ok) {
        return response
      } else {
        // As per fetch specs 4xx errors are not throws but need to be
        // caught in the !response.ok block, for us, we rethrow
        const { data, status, statusText: message } = response ?? {}
        let error = {
          data,
          status,
          message
        }
        throw error
      }
    } catch (err) {
      // As per fetch specs, this handles the 500 and other errors
      throw err
    }
  }
}

const mezmoFetch = new MezmoFetch()
export default mezmoFetch
